import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { VisitExemption } from "@msc/shared";
import { api } from "../lib/api.js";

// ── DTOs ────────────────────────────────────────────────────────────────────

export interface EmployeeRef {
  id: string;
  fullName: string;
  email: string;
}

export interface CampaignRef {
  id: string;
  title: string;
}

export type VisitStatus = "planned" | "done" | "postponed" | "cancelled";

/** Erinnerungs-Ampel: rot = überfällig, orange = bald fällig, grün = OK */
export type ReminderColor = "red" | "orange" | "green";

export interface Visit {
  id: string;
  customerId: string;
  customerName: string;
  /** Segment, dem der Termin zugeordnet ist (T11 — pro (Kunde × Segment)). */
  segmentId: string;
  segmentName?: string;
  scheduledAt: string; // ISO date
  status: VisitStatus;
  shortMinutes: string | null;
  successorVisitId: string | null;
  attendees: EmployeeRef[];
  campaigns: CampaignRef[];
  createdBy: EmployeeRef;
  createdAt: string;
  updatedAt: string;
}

export interface VisitsPage {
  items: Visit[];
  total: number;
  page: number;
  pageSize: number;
}

/**
 * Eine Reminder-Zeile pro (Kunde × Segment) — siehe FK §3.13 Teil B / TK §4.7 / T11.
 * Ein Multi-Segment-Kunde erzeugt mehrere Items mit potenziell unterschiedlichen Status.
 */
export interface ReminderItem {
  customerId: string;
  customerName: string;
  segmentId: string;
  segmentName: string;
  status: ReminderColor | "none" | "exempt";
  lastVisitDate: string | null;
  nextPlannedDate: string | null;
  quadrant: string | null;
  frequencyMonths: number | null;
  daysSinceLast: number | null;
  /** Hinweis unter dem Badge: Ausnahme läuft ab / Verlängerung beantragt / Antrag offen. */
  hint: "laeuft_ab" | "verlaengerung_beantragt" | "beantragt" | null;
  /** Gültige (approved) Ausnahme der Zelle, sonst der offene (requested) Antrag. */
  exemption: VisitExemption | null;
  /** Offener Verlängerungsantrag neben einer gültigen Ausnahme. */
  renewal: VisitExemption | null;
}

export interface RemindersResponse {
  red: number;
  orange: number;
  green: number;
  exempt: number;
  items: ReminderItem[];
}

// ── Query Keys ───────────────────────────────────────────────────────────────

export const visitKeys = {
  all: ["visits"] as const,
  lists: () => [...visitKeys.all, "list"] as const,
  list: (opts: UseVisitsOpts) => [...visitKeys.lists(), opts] as const,
  reminders: (onlyMine?: boolean) => [...visitKeys.all, "reminders", onlyMine ?? "kaskade"] as const,
  customerVisits: (customerId: string) =>
    [...visitKeys.all, "customer", customerId] as const,
};

// ── Query Hooks ──────────────────────────────────────────────────────────────

export interface UseVisitsOpts {
  status?: VisitStatus | VisitStatus[];
  segment?: string;
  due?: string;
  page?: number;
  onlyMine?: boolean;
}

export function useVisits(opts: UseVisitsOpts = {}) {
  const params = new URLSearchParams();

  if (opts.status) {
    const statuses = Array.isArray(opts.status) ? opts.status : [opts.status];
    for (const s of statuses) {
      params.append("status", s);
    }
  }
  if (opts.segment) params.set("segment", opts.segment);
  if (opts.due) params.set("due", opts.due);
  if (opts.onlyMine !== undefined) params.set("onlyMine", String(opts.onlyMine));
  params.set("page", String(opts.page ?? 1));

  const qs = params.toString();
  const path = `/visits${qs ? `?${qs}` : ""}`;

  return useQuery<VisitsPage>({
    queryKey: visitKeys.list(opts),
    queryFn: () => api.get<VisitsPage>(path),
  });
}

export function useVisitReminders(onlyMine?: boolean) {
  const qs = onlyMine === undefined ? "" : `?onlyMine=${onlyMine}`;
  return useQuery<RemindersResponse>({
    queryKey: visitKeys.reminders(onlyMine),
    queryFn: () => api.get<RemindersResponse>(`/visits/reminders${qs}`),
  });
}

export function useCustomerVisits(customerId: string) {
  return useQuery<Visit[]>({
    queryKey: visitKeys.customerVisits(customerId),
    queryFn: async () => {
      const page = await api.get<{ items: Visit[]; total: number }>(
        `/customers/${customerId}/visits`,
      );
      return page.items;
    },
    enabled: Boolean(customerId),
  });
}

// ── Mutation Payloads ────────────────────────────────────────────────────────

export interface CreateVisitPayload {
  customerId: string;
  /** Pflicht ab T11: Termine sind segment-spezifisch (FK §3.13 Teil B). */
  segmentId: string;
  scheduledAt: string; // ISO date string
  attendeeIds: string[];
  linkedCampaignIds?: string[];
  shortMinutes?: string;
}

export interface UpdateVisitPayload {
  scheduledAt?: string;
  status?: VisitStatus;
  attendeeIds?: string[];
  shortMinutes?: string;
}

export interface PostponeVisitPayload {
  newScheduledAt: string; // ISO date string
}

// ── Mutation Hooks ───────────────────────────────────────────────────────────

export function useCreateVisit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateVisitPayload) =>
      api.post<Visit>("/visits", payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: visitKeys.all });
    },
  });
}

export function useUpdateVisit(visitId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateVisitPayload) =>
      api.patch<Visit>(`/visits/${visitId}`, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: visitKeys.all });
    },
  });
}

export function usePostponeVisit(visitId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: PostponeVisitPayload) =>
      api.post<Visit>(`/visits/${visitId}/postpone`, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: visitKeys.all });
    },
  });
}
